import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, StatusBadge, Spinner } from "@/app/ui";
import { Globe, Copy } from "lucide-react";

function WebsiteInner() {
  const { user } = useAuth();
  const { data, loading, refetch } = useFetch("/website-request");
  const [form, setForm] = useState({ business_name: "", domain: "", service_areas: "", about: "", social_links: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.request) setForm({ business_name: data.request.business_name || "", domain: data.request.domain || "", service_areas: data.request.service_areas || "", about: data.request.about || "", social_links: data.request.social_links || "" });
    else if (user?.business) setForm((f) => ({ ...f, business_name: user.business.name }));
  }, [data]); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try { await api.post("/website-request", form); toast.success("Website request submitted"); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const enquiryUrl = `${window.location.origin}/website-enquiry/${user?.business_id}`;
  if (loading) return <Spinner />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Website Add-on" description="Let Amden build and manage your business website." />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 amden-card p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-navy">Website details</h3>
            {data?.request && <StatusBadge status={data.request.status} />}
          </div>
          {[["Business name", "business_name"], ["Preferred domain", "domain"], ["Service areas", "service_areas"], ["Social links", "social_links"]].map(([l, k]) => (
            <div key={k}><label className="text-sm font-medium text-slate-700">{l}</label>
              <input value={form[k]} onChange={set(k)} data-testid={`website-${k}`} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
          ))}
          <div><label className="text-sm font-medium text-slate-700">About your business</label>
            <textarea value={form.about} onChange={set("about")} rows={3} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
          <Btn variant="accent" onClick={save} disabled={saving} data-testid="website-submit">{saving ? "Submitting…" : data?.request ? "Update request" : "Submit request"}</Btn>
        </div>

        <div className="space-y-4">
          <div className="amden-card p-6">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-blue-50 text-blue-accent"><Globe size={22} /></span>
            <h3 className="mt-3 font-semibold text-navy">Enquiries become leads</h3>
            <p className="text-sm text-slate-500 mt-1">Share your enquiry link — every submission auto-creates a lead tagged "Website".</p>
            <div className="mt-3 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <span className="text-xs text-slate-500 truncate flex-1">{enquiryUrl}</span>
              <button onClick={() => { navigator.clipboard.writeText(enquiryUrl); toast.success("Link copied"); }} className="text-slate-400 hover:text-blue-accent"><Copy size={15} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WebsiteAddon() { return <PlanGate feature="website"><WebsiteInner /></PlanGate>; }
